(() => {
  'use strict';

  const SH = window.SassyHive;
  if (!SH || !SH.quiz) return;

  const get = (id) => document.getElementById(id);
  const clean = (id) => get(id)?.value?.trim() || '';

  // There are exactly two preference-exclusion fields. The old separate
  // "colors to avoid" control is retained only as a hidden compatibility
  // field so existing saved customizations can still be restored safely.
  const oldColorAvoid = get('avoidColors');
  if (oldColorAvoid) {
    const oldColorField = oldColorAvoid.closest('.field');
    if (oldColorField) {
      const hiddenColorAvoid = document.createElement('input');
      hiddenColorAvoid.type = 'hidden';
      hiddenColorAvoid.id = 'avoidColors';
      hiddenColorAvoid.value = oldColorAvoid.value || '';
      oldColorField.replaceWith(hiddenColorAvoid);
    }
  }

  const step = document.querySelector('.quiz-step[data-step="5"]');
  if (!step) return;

  step.innerHTML = `
    <div class="eyebrow">Step 5 of 9</div>
    <h2 tabindex="-1">Your Exclusions &amp; Allergies</h2>
    <p class="lead">You may list up to two preference exclusions. Allergy and safety information does not count toward those two exclusions.</p>

    <div class="field">
      <label for="exclusionOne">Exclusion 1</label>
      <textarea id="exclusionOne" rows="3" maxlength="300" data-character-count="ExclusionOneCount" placeholder="List one item, style, theme, color, or product type you do not want included."></textarea>
      <span id="ExclusionOneCount" class="field-character-count" aria-live="polite">0/300</span>
    </div>

    <div class="field" style="margin-top:1rem">
      <label for="exclusionTwo">Exclusion 2</label>
      <textarea id="exclusionTwo" rows="3" maxlength="300" data-character-count="ExclusionTwoCount" placeholder="List one additional item, style, theme, color, or product type you do not want included."></textarea>
      <span id="ExclusionTwoCount" class="field-character-count" aria-live="polite">0/300</span>
    </div>

    <div class="field" style="margin-top:1rem">
      <label for="allergySafety">Allergies / Safety Concerns</label>
      <textarea id="allergySafety" rows="4" maxlength="750" data-character-count="AllergySafetyCount" placeholder="List any ingredients, materials, or product types you must avoid because of an allergy or safety concern."></textarea>
      <span id="AllergySafetyCount" class="field-character-count" aria-live="polite">0/750</span>
      <span class="micro">Please describe products, ingredients, or materials to avoid rather than sharing a medical diagnosis.</span>
    </div>

    <div class="notice sensitivity-guidance" style="margin-top:1rem">
      <strong>Please limit preference exclusions to two.</strong> Allergy and safety information does not count toward those two exclusions. We use allergy information to guide curation, but we cannot guarantee that any product is allergen-free. Always review the individual product label, ingredients, directions, and manufacturer warnings before use.
    </div>

    <input type="hidden" id="spoilItems" value="">
    <input type="hidden" id="mustNotInclude" value="">
  `;

  const syncLegacyFields = () => {
    const legacyExclusions = get('spoilItems');
    const legacySafety = get('mustNotInclude');
    const legacyColor = get('avoidColors');
    if (legacyExclusions) legacyExclusions.value = [clean('exclusionOne'), clean('exclusionTwo')].filter(Boolean).join(' | ');
    if (legacySafety) legacySafety.value = clean('allergySafety');
    if (legacyColor) legacyColor.value = '';
  };

  ['exclusionOne', 'exclusionTwo', 'allergySafety'].forEach((id) => {
    const control = get(id);
    if (!control) return;
    const counter = get(control.dataset.characterCount);
    const updateCounter = () => {
      if (counter) counter.textContent = `${control.value.length}/${control.maxLength}`;
    };
    control.addEventListener('input', () => {
      updateCounter();
      syncLegacyFields();
    });
    control.addEventListener('change', syncLegacyFields);
    updateCounter();
  });

  const originalBuildBoxProperties = SH.quiz.buildBoxProperties.bind(SH.quiz);
  SH.quiz.buildBoxProperties = (video, customizationId) => {
    syncLegacyFields();
    const properties = originalBuildBoxProperties(video, customizationId);
    delete properties['Colors to Avoid, If Possible'];
    delete properties['Items Preferred Not to Receive'];
    delete properties['Items That Would Spoil the Fun'];
    delete properties['Must Not Include'];

    const exclusionOne = clean('exclusionOne');
    const exclusionTwo = clean('exclusionTwo');
    const allergySafety = clean('allergySafety');
    if (exclusionOne) properties['Exclusion 1'] = exclusionOne;
    if (exclusionTwo) properties['Exclusion 2'] = exclusionTwo;
    if (allergySafety) properties['Allergies / Safety Concerns'] = allergySafety;
    return properties;
  };

  const originalRestoreFromProperties = SH.quiz.restoreFromProperties.bind(SH.quiz);
  SH.quiz.restoreFromProperties = (payload) => {
    const restored = originalRestoreFromProperties(payload);
    if (!restored) return restored;

    const properties = payload?.properties || {};
    const exclusionOne = get('exclusionOne');
    const exclusionTwo = get('exclusionTwo');
    const allergySafety = get('allergySafety');
    const oldPreference = properties['Items Preferred Not to Receive'] || properties['Items That Would Spoil the Fun'] || '';
    const oldColor = properties['Colors to Avoid, If Possible'] || '';

    if (exclusionOne) exclusionOne.value = properties['Exclusion 1'] || oldPreference || oldColor || '';
    if (exclusionTwo) exclusionTwo.value = properties['Exclusion 2'] || (oldPreference && oldColor ? oldColor : '') || '';
    if (allergySafety) allergySafety.value = properties['Allergies / Safety Concerns'] || properties['Must Not Include'] || '';

    syncLegacyFields();
    ['exclusionOne', 'exclusionTwo', 'allergySafety'].forEach((id) => get(id)?.dispatchEvent(new Event('input', { bubbles: true })));
    return restored;
  };

  let reviewObserver = null;
  const formatReview = () => {
    const review = get('reviewBox');
    if (!review) return;
    const profile = review.querySelectorAll('.summary-card')[1];
    if (!profile) return;

    if (reviewObserver) reviewObserver.disconnect();

    const oldParagraphs = [...profile.querySelectorAll('p')].filter((paragraph) => {
      const label = paragraph.querySelector('strong')?.textContent?.trim() || '';
      return label === 'Colors to avoid, if possible:' || label === 'Items preferred not to receive:' || label === 'Must not include:';
    });
    oldParagraphs.forEach((paragraph) => paragraph.remove());

    [...profile.querySelectorAll('p[data-sassy-exclusion-line]')].forEach((paragraph) => paragraph.remove());
    const before = [...profile.querySelectorAll('p')].find((paragraph) => paragraph.querySelector('strong')?.textContent?.trim() === 'Gold or silver preference:') || null;

    const lines = [
      ['Exclusion 1', clean('exclusionOne')],
      ['Exclusion 2', clean('exclusionTwo')],
      ['Allergies / Safety Concerns', clean('allergySafety')]
    ];
    lines.forEach(([label, value]) => {
      if (!value) return;
      const paragraph = document.createElement('p');
      paragraph.dataset.sassyExclusionLine = 'true';
      const strong = document.createElement('strong');
      strong.textContent = `${label}:`;
      paragraph.append(strong, document.createTextNode(` ${value}`));
      profile.insertBefore(paragraph, before);
    });

    if (reviewObserver) reviewObserver.observe(review, { childList: true, subtree: true });
  };

  const review = get('reviewBox');
  if (review) {
    reviewObserver = new MutationObserver(formatReview);
    reviewObserver.observe(review, { childList: true, subtree: true });
  }

  const patchSafetyAccordion = () => {
    document.querySelectorAll('.policy-accordion').forEach((accordion) => {
      const summary = accordion.querySelector('summary')?.textContent?.trim();
      if (summary !== 'Safety & Sensitivities') return;
      accordion.querySelectorAll('p').forEach((paragraph) => {
        if (!paragraph.textContent.includes('must not include')) return;
        paragraph.textContent = paragraph.textContent
          .replace('The “must not include” field', 'The “Allergies / Safety Concerns” field')
          .replace('The "must not include" field', 'The “Allergies / Safety Concerns” field');
      });
    });
  };

  patchSafetyAccordion();

  window.setTimeout(() => {
    const legacyExclusions = get('spoilItems')?.value || '';
    const legacyColor = get('avoidColors')?.value || '';
    const legacySafety = get('mustNotInclude')?.value || '';

    if (!clean('exclusionOne')) get('exclusionOne').value = legacyExclusions || legacyColor || '';
    if (!clean('exclusionTwo') && legacyExclusions && legacyColor) get('exclusionTwo').value = legacyColor;
    if (!clean('allergySafety') && legacySafety) get('allergySafety').value = legacySafety;

    ['exclusionOne', 'exclusionTwo', 'allergySafety'].forEach((id) => get(id)?.dispatchEvent(new Event('input', { bubbles: true })));
    formatReview();
  }, 20);
})();
